// LinkLibraryDrawer — Jun 2026 · PIPELINE_SIMPLIFICATION_PROJECT.md item F
//
// Randy's idea, and a better answer than the four index-time naming rules it
// replaced. Those all tried to GUESS a good name from the text around a URL.
// This lets him fix the ten names that matter, and the guessing becomes
// unnecessary.
//
// One ownership rule, his words: "its growth add to, doesnt overwrite chnages
// they've made." A row he touches is never overwritten by a re-index.
import { useCallback, useEffect, useState } from "react";
import {
  CaretRight, CaretDown, Spinner, Eye, EyeSlash, PushPin, Check, X,
  Plus, Trash, MagnifyingGlass, PencilSimple, YoutubeLogo, LinkSimple,
  MagicWand, Broom, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Where a name came from, in the owner's language. Four of these five cost
// nothing and are exact; only the last one asks a model.
const SOURCE_LABEL = {
  sibling_row: "another row for the same link",
  silo_file_title: "your own file for that video",
  youtube_title: "YouTube itself",
  heading: "the heading above it in your description",
  destination: "the address itself",
  model: "your AI",
};

const PROBLEM_LABEL = {
  "no name at all": "no name",
  "bare fragment": "just a fragment",
  "markdown fragment": "formatting leftover",
  "prose": "a sentence, not a name",
  "the creator talking": "you talking, not a name",
  "cut off mid-line": "cut off mid-line",
  "too short": "too short",
};

function Row({ siloId, row, onChanged, onPin, onEdited, pinned = false, was }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(row.label || "");
  const [editingUrl, setEditingUrl] = useState(false);
  const [url, setUrl] = useState(row.url || "");
  const [busy, setBusy] = useState(false);
  const [fixing, setFixing] = useState(false);

  const patch = async (body) => {
    setBusy(true);
    try {
      await api.patch(`/silos/${siloId}/link-library/${encodeURIComponent(row.id)}`, body);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that.");
    } finally { setBusy(false); }
  };

  // Jun 2026 (LINKS_WHY_MISSING_EVAL.md round 2) — Randy's per-row button.
  // It tries his own data first (a sibling row, a file he has for that video,
  // YouTube's own title for it, the heading the link sits under) and only then
  // asks a model, because an exact title beats a paraphrase of that same
  // title. In DEEP mode it will not hand back the name already on the row.
  const fixName = async () => {
    setFixing(true);
    try {
      const r = await api.post(`/silos/${siloId}/link-library/${encodeURIComponent(row.id)}/fix-name`);
      if (r.data?.ok) {
        toast.success(`Named from ${SOURCE_LABEL[r.data.source] || "your library"}.`, {
          description: r.data.link?.label || undefined,
        });
        onPin?.(row.id);
        // A single fix is a "Just edited" group of one — Randy: *"be it a group
        // or a single"* — and it replaces whatever the last fix left on screen.
        onEdited?.([{
          id: row.id,
          before: row.label || "",
          after: r.data.link?.label || "",
          source: r.data.source || "",
        }]);
        onChanged();
      } else {
        toast.message(r.data?.detail || "Couldn't work out what that link is.", {
          duration: 9000,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Name fix failed.");
    } finally { setFixing(false); }
  };

  // Jun 2026 (Randy) — "they may investigate by clicking on the link only to
  // find that it leads to an expired or dead end." The old address is recorded
  // server-side so a re-index of the file that still carries it cannot bring
  // it back.
  const saveUrl = async () => {
    const next = (url || "").trim();
    if (!next || next === row.url) { setUrl(row.url || ""); setEditingUrl(false); return; }
    setBusy(true);
    try {
      await api.patch(`/silos/${siloId}/link-library/${encodeURIComponent(row.id)}`, { url: next });
      toast.success("Address updated.", {
        description: "Your files can't bring the old one back.",
      });
      setEditingUrl(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that address.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/silos/${siloId}/link-library/${encodeURIComponent(row.id)}`);
      toast.success("Link removed.");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove that.");
    } finally { setBusy(false); }
  };

  const saveLabel = async () => {
    if ((label || "").trim() === (row.label || "").trim()) { setEditing(false); return; }
    onPin?.(row.id);
    await patch({ label: label.trim() });
    setEditing(false);
  };

  return (
    <div
      data-testid={`link-row-${row.id}`}
      className={`flex items-start gap-2.5 border-b hairline px-2.5 py-2 last:border-b-0 ${row.hidden ? "opacity-55" : ""} ${
        pinned ? "bg-[#F3F6F2] shadow-[inset_2px_0_0_#4A5D4E]" : ""
      }`}
    >
      {row.is_youtube
        ? <YoutubeLogo size={14} weight="duotone" className="mt-1 shrink-0 text-[#B4432F]" />
        : <LinkSimple size={14} weight="duotone" className="mt-1 shrink-0 text-[#3A4A3E]" />}

      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input
              data-testid={`link-label-input-${row.id}`}
              value={label}
              autoFocus
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveLabel();
                if (e.key === "Escape") { setLabel(row.label || ""); setEditing(false); }
              }}
              placeholder="What should this link be called?"
              className="min-w-0 flex-1 rounded-md border hairline bg-white px-2 py-1 text-xs"
            />
            <button
              type="button"
              data-testid={`link-label-save-${row.id}`}
              onClick={saveLabel}
              disabled={busy}
              className="rounded border hairline bg-white p-1 text-[#2F6B3A] hover:border-[#4A5D4E]"
              aria-label="Save name"
            ><Check size={12} /></button>
            <button
              type="button"
              onClick={() => { setLabel(row.label || ""); setEditing(false); }}
              className="rounded border hairline bg-white p-1 text-[#6B6A68] hover:border-[#4A5D4E]"
              aria-label="Cancel"
            ><X size={12} /></button>
          </div>
        ) : (
          <button
            type="button"
            data-testid={`link-label-${row.id}`}
            onClick={() => setEditing(true)}
            className="group flex max-w-full items-center gap-1.5 text-left"
          >
            <span className={`truncate text-xs ${row.label ? "text-[#1C1B1A]" : "italic text-[#9B9992]"}`}>
              {row.label || "no name — shows as a bare link"}
            </span>
            <PencilSimple size={11} className="shrink-0 text-[#B9B7B0] group-hover:text-[#3A4A3E]" />
          </button>
        )}
        {was !== undefined && was !== (row.label || "") && (
          <div
            data-testid={`link-was-${row.id}`}
            className="mt-0.5 truncate text-[10.5px] text-[#9B9992]"
            title={was || "no name"}
          >
            was: <span className="italic">{was || "no name"}</span>
          </div>
        )}

        {editingUrl ? (
          <div className="mt-1 flex items-center gap-1.5" data-testid={`link-url-edit-row-${row.id}`}>
            <input
              data-testid={`link-url-input-${row.id}`}
              value={url}
              autoFocus
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveUrl();
                if (e.key === "Escape") { setUrl(row.url || ""); setEditingUrl(false); }
              }}
              placeholder="https://…"
              className="min-w-0 flex-1 rounded-md border hairline bg-white px-2 py-1 text-[11px]"
            />
            <button
              type="button"
              data-testid={`link-url-save-${row.id}`}
              onClick={saveUrl}
              disabled={busy}
              className="rounded border hairline bg-white p-1 text-[#2F6B3A] hover:border-[#4A5D4E]"
              aria-label="Save address"
            ><Check size={12} /></button>
            <button
              type="button"
              data-testid={`link-url-cancel-${row.id}`}
              onClick={() => { setUrl(row.url || ""); setEditingUrl(false); }}
              className="rounded border hairline bg-white p-1 text-[#6B6A68] hover:border-[#4A5D4E]"
              aria-label="Cancel"
            ><X size={12} /></button>
          </div>
        ) : (
          <div className="mt-0.5 flex items-center gap-1.5">
            <a
              href={row.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`link-url-${row.id}`}
              className="min-w-0 truncate text-[11px] text-[#6B6A68] hover:text-[#3A4A3E] hover:underline"
            >
              {row.url}
            </a>
            <button
              type="button"
              data-testid={`link-url-edit-${row.id}`}
              onClick={() => { setUrl(row.url || ""); setEditingUrl(true); }}
              title="Fix this address — dead link, expired listing, moved page"
              className="shrink-0 text-[#B9B7B0] hover:text-[#3A4A3E]"
              aria-label="Edit address"
            ><PencilSimple size={11} /></button>
          </div>
        )}

        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-[#9B9992]">
          {row.manual
            ? <span className="text-[#7A5C3E]">added by hand</span>
            : <span className="truncate max-w-[22rem]">from “{row.source_title || "a file"}”</span>}
          {row.also_in_docs?.length > 0 && (
            <span data-testid={`link-alsoin-${row.id}`}>+{row.also_in_docs.length} more file{row.also_in_docs.length > 1 ? "s" : ""}</span>
          )}
          {row.duplicate_of > 0 && (
            <span data-testid={`link-dupe-${row.id}`} className="text-[#A8741A]">
              +{row.duplicate_of} duplicate{row.duplicate_of > 1 ? "s" : ""}
            </span>
          )}
          {row.name_problem && (
            <span data-testid={`link-problem-${row.id}`} className="text-[#A8741A]">
              {PROBLEM_LABEL[row.name_problem] || row.name_problem}
            </span>
          )}
          {row.owner_touched && <span className="text-[#2F6B3A]">yours · survives re-index</span>}
          {pinned && (
            <span data-testid={`link-pinned-note-${row.id}`} className="text-[#4A5D4E]">
              just changed · kept in view
            </span>
          )}
          {row.replaced_url && (
            <span data-testid={`link-url-fixed-${row.id}`} className="text-[#3A5A7A]">
              address fixed by you
            </span>
          )}
          {row.evergreen_pin && <span className="text-[#3A5A7A]">pinned</span>}
          {row.hidden && <span className="text-[#B4432F]">hidden</span>}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {!row.manual && (
          <button
            type="button"
            data-testid={`link-fix-${row.id}`}
            onClick={fixName}
            disabled={fixing || busy}
            title="Work out what this link is and name it"
            className={`rounded border hairline bg-white p-1 hover:border-[#4A5D4E] disabled:opacity-50 ${row.name_problem ? "text-[#A8741A]" : "text-[#B9B7B0]"}`}
          >
            {fixing ? <Spinner size={12} className="animate-spin" /> : <MagicWand size={12} />}
          </button>
        )}
        <button
          type="button"
          data-testid={`link-pin-${row.id}`}
          onClick={() => patch({ evergreen_pin: !row.evergreen_pin })}
          disabled={busy}
          title={row.evergreen_pin ? "Stop treating this as a channel-level link" : "Always eligible as a fallback link"}
          className={`rounded border hairline bg-white p-1 hover:border-[#4A5D4E] ${row.evergreen_pin ? "text-[#3A5A7A]" : "text-[#B9B7B0]"}`}
        ><PushPin size={12} weight={row.evergreen_pin ? "fill" : "regular"} /></button>
        <button
          type="button"
          data-testid={`link-hide-${row.id}`}
          onClick={() => patch({ hidden: !row.hidden })}
          disabled={busy}
          title={row.hidden ? "Offer this link again" : "Never offer this link"}
          className={`rounded border hairline bg-white p-1 hover:border-[#4A5D4E] ${row.hidden ? "text-[#B4432F]" : "text-[#B9B7B0]"}`}
        >{row.hidden ? <EyeSlash size={12} /> : <Eye size={12} />}</button>
        {row.manual && (
          <button
            type="button"
            data-testid={`link-delete-${row.id}`}
            onClick={remove}
            disabled={busy}
            title="Delete this link"
            className="rounded border hairline bg-white p-1 text-[#B9B7B0] hover:border-[#B4432F] hover:text-[#B4432F]"
          ><Trash size={12} /></button>
        )}
      </div>
    </div>
  );
}

export default function LinkLibraryDrawer({ siloId, linkCount = 0 }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [limit, setLimit] = useState(60);
  const [health, setHealth] = useState(null);
  const [working, setWorking] = useState("");
  const [confirmRebuild, setConfirmRebuild] = useState(false);
  const [progress, setProgress] = useState(null);
  // LINKS_CONSOLE_SPRINT.md · §7 — rows held visible through a search they no
  // longer match. Randy searches a bad name, fixes that one row, and the new
  // name stops matching the query he is still looking at, so the row vanishes
  // before he can see what it became. A single-row fix pins the row until he
  // changes the search or the filter himself. Batch fixes deliberately do NOT
  // pin: 24 pinned rows is not a review surface.
  const [pinnedIds, setPinnedIds] = useState(() => new Set());
  const pinRow = useCallback((id) => {
    setPinnedIds((prev) => new Set(prev).add(id));
  }, []);
  useEffect(() => { setPinnedIds(new Set()); }, [q, filter]);

  // LINK_LIBRARY_AUTOFIX_PROJECT.md · Phase C — "Just edited". Randy, after
  // fixing a batch of eight and watching all eight leave the screen: *"all I
  // would want is the current list of whatever needs fixed would simply not
  // vanish, but show their changed state in place… or we create a new group,
  // called 'Just edited' that appears after an edit, be it a group or a single.
  // It could stick around until another single or group change happens."*
  //
  // So: id → {before, after, source}. Rows in here are held on screen whatever
  // the active filter says (the "Needs a name" filter stops matching the moment
  // the name is fixed, which is exactly how the batch disappeared), each shows
  // what it used to say, and the whole set is REPLACED by the next fix rather
  // than accumulating. Not persisted — it describes this visit.
  const [justEdited, setJustEdited] = useState(() => new Map());
  const noteEdits = useCallback((list) => {
    const next = new Map();
    for (const c of list || []) if (c?.id) next.set(c.id, c);
    setJustEdited(next);
    if (next.size) setFilter("fresh");
  }, []);
  // Oct 23, 2026 (Randy) — *"it almost does it too well. lol … the only way to
  // get the list (just fixed) to go away, is to refresh the page… a press of the
  // 'all' button, or even taping on the search field and adding a charactor,
  // removing a charactor, should also just reset the list to normal."*
  //
  // The hold above is deliberately stronger than the pins — it survives the
  // filter a fix was run from, because that filter is the one that stops
  // matching. But nothing released it, so a search typed afterwards still
  // dragged the whole fixed group along. Any move HE makes now ends the review:
  // typing in the search, or choosing any filter other than the group itself.
  // Leaving "Just edited" drops the chip, so the view goes back to All rather
  // than to an empty list.
  const endReview = useCallback(() => {
    setJustEdited((prev) => (prev.size ? new Map() : prev));
    setFilter((f) => (f === "fresh" ? "all" : f));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/silos/${siloId}/link-library`);
      setData(r.data);
      // Jun 2026 (Randy) — keep the Settings-tab pip honest as he works: the
      // full list already carries both counts, so re-deriving them here saves
      // a second request and the dot clears the moment a row is fixed.
      setHealth((h) => {
        const needs = r.data?.needs_name || 0;
        const dupes = r.data?.duplicates || 0;
        const enabled = h?.enabled !== false;
        const next = {
          enabled, needs_name: needs, duplicates: dupes,
          pending: enabled ? needs + (dupes >= 5 ? dupes : 0) : 0,
          total: r.data?.total || 0,
          last_pass: r.data?.last_pass ?? h?.last_pass ?? null,
        };
        // Announced AFTER the updater returns, not inside it: React can call an
        // updater twice, and dispatching from in there warned about a state
        // update during render.
        queueMicrotask(() => window.dispatchEvent(
          new CustomEvent("links:changed", { detail: next })));
        return next;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open your link library.");
    } finally { setLoading(false); }
  }, [siloId]);

  // The closed drawer still has to say that something is pending — the pip on
  // the tab says WHERE to look, this says WHAT.
  useEffect(() => {
    if (!siloId) return;
    let cancelled = false;
    api.get(`/silos/${siloId}/link-library/health`)
      .then((r) => { if (!cancelled) setHealth(r.data); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [siloId]);

  useEffect(() => { if (open && !data) load(); }, [open, data, load]);

  // Free and instant: merge rows pointing at the same destination, and drop
  // links that came from somebody else's comment.
  // Jun 2026 — "start completely clean". The link rows are derived data: every
  // one of them is re-readable from the files, so the honest repair after the
  // purge bug ate a production library is to throw the table away and read the
  // files again. Two taps, because it also drops hand-named rows.
  const rebuild = async () => {
    if (confirmRebuild !== true) { setConfirmRebuild(true); return; }
    setConfirmRebuild(false);
    setWorking("rebuild");
    try {
      const r = await api.post(`/silos/${siloId}/link-library/rebuild`);
      const d = r.data || {};
      toast.success(
        `Rebuilt — ${d.after} link${d.after === 1 ? "" : "s"} read back from ${d.documents} file${d.documents === 1 ? "" : "s"}.`,
        d.kept_manual ? { description: `Your ${d.kept_manual} typed-in link${d.kept_manual === 1 ? "" : "s"} were kept — no file can rebuild those.` } : undefined,
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rebuild failed.");
    } finally { setWorking(""); }
  };

  // Oct 25, 2026 — the safe half of "start fresh": the same re-read of the
  // same files, except a name he typed is left alone and only the machine's
  // names are refreshed from whatever the files now say.
  const reread = async () => {
    setWorking("reread");
    try {
      const r = await api.post(`/silos/${siloId}/link-library/reread`);
      const d = r.data || {};
      toast.success(
        `Re-read ${d.documents} file${d.documents === 1 ? "" : "s"} — ${d.rows_now} link${d.rows_now === 1 ? "" : "s"}.`,
        { description: `${d.kept_yours} name${d.kept_yours === 1 ? "" : "s"} you typed were kept · ${d.needs_name} still flagged (was ${d.needs_name_before}).` },
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-read failed.");
    } finally { setWorking(""); }
  };

  const tidy = async () => {
    setWorking("tidy");
    try {
      const r = await api.post(`/silos/${siloId}/link-library/tidy`);
      const d = r.data || {};
      const bits = [];
      if (d.duplicates_removed) bits.push(`merged ${d.duplicates_removed} duplicate${d.duplicates_removed > 1 ? "s" : ""}`);
      if (d.foreign_removed) bits.push(`dropped ${d.foreign_removed} viewer link${d.foreign_removed > 1 ? "s" : ""}`);
      // Oct 2026 — a video row that carried nothing but its own title now
      // borrows the opening of its description, so it competes on what it is
      // about. Worth saying out loud: it is why a cold-pull video started
      // winning the question it answers.
      if (d.contexts_enriched) bits.push(`gave ${d.contexts_enriched} video link${d.contexts_enriched > 1 ? "s" : ""} their description`);
      toast.success(bits.length ? `Tidied — ${bits.join(", ")}.` : "Nothing to tidy — already one row per link.");
      // Oct 2026 (§8 concern 1) — a merge changes which NAME a destination
      // shows under, and that used to vanish into a bare count. Hold the
      // survivors on screen like any other fix.
      noteEdits(d.changed || []);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tidy failed.");
    } finally { setWorking(""); }
  };

  // Walks the library in batches so a 300-row run shows progress instead of
  // hanging on one request.
  //
  // The cursor rule is the subtle part. The list shrinks as rows get named, so
  // an advancing offset overshoots and leaves the tail untouched. So: advance
  // only when a pass changed nothing — which is exactly how it steps over the
  // rows that genuinely cannot be named — and start again from the top
  // whenever the list got shorter.
  const fixNames = async () => {
    setWorking("unnamed");
    setProgress({ renamed: 0, processed: 0, remaining: null });
    let renamed = 0, processed = 0, offset = 0, guard = 0;
    const edits = [];
    try {
      for (;;) {
        guard += 1;
        if (guard > 60) break;
        const r = await api.post(`/silos/${siloId}/link-library/fix-names`, {
          limit: 10, offset,
        });
        const d = r.data || {};
        renamed += d.renamed || 0;
        processed += d.processed || 0;
        edits.push(...(d.changed || []));
        setProgress({ renamed, processed, remaining: d.remaining ?? 0 });
        if (!d.processed) break;
        if (d.renamed > 0) offset = 0;
        else offset = d.next_offset ?? (offset + 10);
        if (!d.needs_name) break;
      }
      toast.success(renamed
        ? `Fixed ${renamed} name${renamed === 1 ? "" : "s"}.`
        : "Nothing left that your files can explain.");
      await load();
      noteEdits(edits);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Naming failed.");
      await load();
      noteEdits(edits);
    } finally { setWorking(""); setProgress(null); }
  };

  // LINKS_CONSOLE_SPRINT.md · §10 — every YouTube row gets the video's real
  // title, whether or not its current name looked broken. Free: oEmbed, no
  // model, no key, nothing spent. Hand edits are skipped — Randy: *"Hand edits
  // are always going to be of highest regard."* The offset only ever advances,
  // because this pass doesn't shrink its own list.
  const refreshYouTube = async () => {
    setWorking("youtube");
    setProgress({ renamed: 0, processed: 0, remaining: null });
    let renamed = 0, processed = 0, unreachable = 0, offset = 0, guard = 0;
    const edits = [];
    try {
      for (;;) {
        guard += 1;
        if (guard > 60) break;
        const r = await api.post(`/silos/${siloId}/link-library/youtube-titles`, {
          limit: 10, offset,
        });
        const d = r.data || {};
        renamed += d.renamed || 0;
        processed += d.processed || 0;
        unreachable += d.unreachable || 0;
        edits.push(...(d.changed || []));
        setProgress({ renamed, processed, remaining: d.remaining ?? 0 });
        if (!d.processed || !d.remaining) break;
        offset = d.next_offset ?? (offset + 10);
      }
      toast.success(
        renamed
          ? `Retitled ${renamed} YouTube link${renamed === 1 ? "" : "s"} from YouTube itself.`
          : "Every YouTube link already carries its real title.",
        unreachable
          ? { description: `${unreachable} video${unreachable === 1 ? " was" : "s were"} private, deleted, or unreachable — left alone.` }
          : undefined,
      );
      await load();
      noteEdits(edits);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reach YouTube.");
      await load();
      noteEdits(edits);
    } finally { setWorking(""); setProgress(null); }
  };

  const addLink = async () => {
    if (!newUrl.trim()) return;
    try {
      await api.post(`/silos/${siloId}/link-library`, {
        url: newUrl.trim(), label: newLabel.trim(),
      });
      setNewUrl(""); setNewLabel(""); setAdding(false);
      toast.success("Link added to your library.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add that link.");
    }
  };

  const rows = (data?.links || []).filter((r) => {
    // A row he just fixed by hand stays put, whatever the stale query says.
    if (pinnedIds.has(r.id)) return true;
    // ...and so does a row any fix just changed, for the same reason: the
    // filter he ran the fix FROM is usually the one that stops matching. Held
    // only in the lists a fix is actually run from, so a repair never leaks
    // rows into Hidden, Pinned or Duplicates.
    if (justEdited.has(r.id) && ["fresh", "all", "unnamed"].includes(filter)) return true;
    if (filter === "fresh") return false;
    if (filter === "named" && !r.label) return false;
    if (filter === "unnamed" && !r.name_problem) return false;
    if (filter === "hidden" && !r.hidden) return false;
    if (filter === "pinned" && !r.evergreen_pin) return false;
    if (filter === "yours" && !r.owner_touched) return false;
    if (filter === "dupes" && !(r.duplicate_of > 0)) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (r.label || "").toLowerCase().includes(s)
      || (r.url || "").toLowerCase().includes(s)
      || (r.source_title || "").toLowerCase().includes(s);
  });

  const FILTERS = [
    ["all", "All"], ["unnamed", `Needs a name${data ? ` (${data.needs_name || 0})` : ""}`],
    ...(justEdited.size ? [["fresh", `Just edited (${justEdited.size})`]] : []),
    ["dupes", "Duplicates"], ["yours", "Edited by you"],
    ["pinned", "Pinned"], ["hidden", "Hidden"],
  ];

  return (
    <div className="mt-3" data-testid="link-library-drawer">
      <button
        type="button"
        data-testid="link-library-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-md border hairline bg-white px-3 py-2 text-left text-xs text-[#1C1B1A] hover:border-[#4A5D4E]"
      >
        {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
        <span className="flex-1">
          Link library
          <span className="ml-1.5 text-[#6B6A68]">
            {data ? `${data.unique_urls} addresses in ${data.total} rows` : `${linkCount} links`}
          </span>
        </span>
        {health?.enabled && (health.needs_name > 0 || health.duplicates >= 5) && (
          <>
            {/* LINKS_CONSOLE_SPRINT.md · §6 — the red dot, matching the one on
                the Settings tab. Randy asked for the same mark in three
                places so the trail reads consistently: tab → tool → action.
                The amber count pill beside it says WHAT; this says LOOK. */}
            <span
              data-testid="link-library-header-pip"
              aria-hidden="true"
              className="shrink-0 w-1.5 h-1.5 rounded-full bg-[#E86A5C] ring-1 ring-white/80 shadow-sm"
            />
            <span
              data-testid="link-library-pending-badge"
              className="shrink-0 rounded-full bg-[#FBF0D9] px-2 py-0.5 text-[10px] font-medium text-[#8A6516]"
            >
              {[
                health.needs_name > 0 ? `${health.needs_name} need${health.needs_name === 1 ? "s" : ""} a name` : "",
                health.duplicates >= 5 ? `${health.duplicates} duplicates` : "",
              ].filter(Boolean).join(" · ")}
            </span>
          </>
        )}
        {loading && <Spinner size={12} className="animate-spin text-[#6B6A68]" />}
      </button>

      {open && (
        <div className="mt-2 rounded-md border hairline bg-[#FBFAF6]">
          <p className="border-b hairline px-3 py-2.5 text-[11px] leading-relaxed text-[#6B6A68]">
            Every link your files carry, with the name your silo will say it by. Rename anything
            that reads badly, hide anything you never want offered, and pin the ones that belong
            to the whole channel rather than one video. <strong>Anything you change here survives
            a re-index</strong> — new files add to this list, they never overwrite your edits.
          </p>

          {/* Oct 23, 2026 (Randy) — *"Seeing 77 is confusing becuase user does
              not know that 60 were fixed. to them it just seems like it didnt
              do its job."* After a rebuild or a new file the naming pass runs
              in the background and stops at its cap; this is its receipt, so
              the number on the button reads as "what is left" rather than
              "nothing happened". */}
          {health?.last_pass?.looked > 0 && (
            <p
              data-testid="link-library-last-pass"
              className="border-b hairline bg-[#F4F7F2] px-3 py-2 text-[11px] leading-relaxed text-[#3A4A3E]"
            >
              <strong>Last automatic pass:</strong> looked at {health.last_pass.looked}
              {" "}row{health.last_pass.looked === 1 ? "" : "s"} and named {health.last_pass.named}
              {health.last_pass.model_calls > 0 ? ` (${health.last_pass.model_calls} with your AI)` : ""}.
              {(data?.needs_name || 0) > 0 ? (
                <> {data.needs_name} still need{data.needs_name === 1 ? "s" : ""} a name — one pass
                stops at 60 rows, so press <em>Fix bad names</em> to carry on.</>
              ) : null}
            </p>
          )}

          {/* Jun 2026 (LINKS_WHY_MISSING_EVAL.md round 2) — Randy: "we want to
              get rid of dups, and we want the right name and the right links
              for what's left." Tidy is free and instant; naming tries his own
              data first and only pays for a model on what is left.
              LINKS_CONSOLE_SPRINT.md · §10 — "Rename all" is gone (it targeted
              rows he had named himself, with no confirmation and a full-library
              model bill) and a free YouTube-only pass took its place. */}
          <div className="flex flex-wrap items-center gap-2 border-b hairline bg-white px-3 py-2.5">
            <button
              type="button"
              data-testid="link-library-tidy"
              onClick={tidy}
              disabled={!!working}
              title="Merges rows pointing at the same address, keeping the best name, and drops links that came from a viewer's comment rather than from you. Rows you edited are never dropped."
              className="flex items-center gap-1.5 rounded-md border hairline bg-white px-2.5 py-1.5 text-xs text-[#3A4A3E] hover:border-[#4A5D4E] disabled:opacity-50"
            >
              {working === "tidy" ? <Spinner size={12} className="animate-spin" /> : <Broom size={12} />}
              Tidy duplicates &amp; fan links
            </button>
            <button
              type="button"
              data-testid="link-library-fix-unnamed"
              onClick={fixNames}
              disabled={!!working || !data?.needs_name}
              title="Repairs names that can't be printed as a name: prose, chatty asides, bare fragments like “5 pack”, lines cut off mid-sentence, and names missing entirely. Tries your own files first and only pays for a model on what's left."
              className="relative flex items-center gap-1.5 rounded-md bg-[#3A4A3E] px-2.5 py-1.5 text-xs text-white hover:bg-[#2F3D32] disabled:opacity-40"
            >
              {/* LINKS_CONSOLE_SPRINT.md · §6 — third and last stop on the
                  trail: tab pip → header pip → the action itself. */}
              {!working && (data?.needs_name || 0) > 0 && (
                <span
                  data-testid="link-library-fix-unnamed-pip"
                  aria-hidden="true"
                  className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#E86A5C] ring-1 ring-white shadow-sm"
                />
              )}
              {working === "unnamed" ? <Spinner size={12} className="animate-spin" /> : <MagicWand size={12} />}
              Fix {data?.needs_name || 0} bad name{(data?.needs_name || 0) === 1 ? "" : "s"}
            </button>
            <button
              type="button"
              data-testid="link-library-youtube-titles"
              onClick={refreshYouTube}
              disabled={!!working}
              title="Asks YouTube for the real title of every YouTube link, whether or not its current name looks broken. Free — no AI, nothing spent. Names you typed yourself are left alone."
              className="flex items-center gap-1.5 rounded-md border hairline bg-white px-2.5 py-1.5 text-xs text-[#B54848] hover:border-[#B54848] disabled:opacity-50"
            >
              {working === "youtube" ? <Spinner size={12} className="animate-spin" /> : <YoutubeLogo size={12} weight="fill" />}
              Refresh YouTube titles
            </button>
            <button
              type="button"
              data-testid="link-library-reread"
              onClick={reread}
              disabled={!!working}
              title="Reads every link back out of your files and refreshes the names that were written for you. Names you typed yourself are kept. Free — no AI, nothing spent."
              className="flex items-center gap-1.5 rounded-md border hairline bg-white px-2.5 py-1.5 text-xs text-[#6B6A68] hover:border-[#C16E5A] hover:text-[#C16E5A] disabled:opacity-50"
            >
              {working === "reread" ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
              Re-read my files
            </button>
            <button
              type="button"
              data-testid="link-library-rebuild"
              onClick={rebuild}
              onBlur={() => setConfirmRebuild(false)}
              disabled={!!working}
              title="Throws this list away and reads every link back out of your files. Your files are not touched, and links you typed in by hand are kept."
              className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs disabled:opacity-50 ${
                confirmRebuild
                  ? "border-[#C16E5A] bg-[#C16E5A] text-white"
                  : "hairline bg-white text-[#6B6A68] hover:border-[#C16E5A] hover:text-[#C16E5A]"
              }`}
            >
              {working === "rebuild" ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
              {confirmRebuild ? "Sure? Your renames go too" : "Start fresh from my files"}
            </button>
            {progress && (
              <span className="text-[11px] text-[#6B6A68]" data-testid="link-library-progress">
                {progress.renamed} renamed · {progress.processed} checked
                {progress.remaining ? ` · ${progress.remaining} to go` : ""}
              </span>
            )}
            {!progress && (
              <span className="ml-auto text-[11px] text-[#9B9992]">
                fixing bad names runs on your own AI key · YouTube titles are free
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 border-b hairline px-3 py-2">
            <div className="relative min-w-[10rem] flex-1">
              <MagnifyingGlass size={12} className="absolute left-2 top-2.5 text-[#9B9992]" />
              <input
                data-testid="link-library-search"
                value={q}
                onChange={(e) => { setQ(e.target.value); endReview(); }}
                placeholder="Search names, addresses, files…"
                className="w-full rounded-md border hairline bg-white py-1.5 pl-7 pr-2 text-xs"
              />
            </div>
            <button
              type="button"
              data-testid="link-library-add-toggle"
              onClick={() => setAdding((v) => !v)}
              className="flex shrink-0 items-center gap-1 rounded-md border hairline bg-white px-2 py-1.5 text-xs text-[#3A4A3E] hover:border-[#4A5D4E]"
            ><Plus size={12} /> Add a link</button>
          </div>

          {adding && (
            <div className="flex flex-wrap items-center gap-2 border-b hairline bg-white px-3 py-2" data-testid="link-library-add-form">
              <input
                data-testid="link-library-new-url"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://…"
                className="min-w-[12rem] flex-1 rounded-md border hairline px-2 py-1.5 text-xs"
              />
              <input
                data-testid="link-library-new-label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addLink(); }}
                placeholder="What to call it"
                className="min-w-[10rem] flex-1 rounded-md border hairline px-2 py-1.5 text-xs"
              />
              <button
                type="button"
                data-testid="link-library-add-save"
                onClick={addLink}
                className="shrink-0 rounded-md bg-[#3A4A3E] px-3 py-1.5 text-xs text-white hover:bg-[#2F3D32]"
              >Add</button>
            </div>
          )}

          <div className="flex flex-wrap gap-1.5 border-b hairline px-3 py-2">
            {FILTERS.map(([id, label]) => (
              <button
                key={id}
                type="button"
                data-testid={`link-library-filter-${id}`}
                onClick={() => { setFilter(id); setLimit(60); if (id !== "fresh") endReview(); }}
                className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                  filter === id
                    ? "border-[#3A4A3E] bg-[#3A4A3E] text-white"
                    : "hairline bg-white text-[#6B6A68] hover:border-[#4A5D4E]"
                }`}
              >{label}</button>
            ))}
            {data && (
              <span className="ml-auto self-center text-[11px] text-[#6B6A68]" data-testid="link-library-count">
                {rows.length} shown
              </span>
            )}
          </div>

          <div className="max-h-[28rem] overflow-auto">
            {!data && loading && (
              <div className="flex items-center gap-2 px-3 py-4 text-xs text-[#6B6A68]">
                <Spinner size={12} className="animate-spin" /> Reading your library…
              </div>
            )}
            {data && !rows.length && (
              <p className="px-3 py-4 text-xs text-[#6B6A68]" data-testid="link-library-empty">
                Nothing matches that.
              </p>
            )}
            {rows.slice(0, limit).map((r) => (
              <Row
                key={r.id}
                siloId={siloId}
                row={r}
                onChanged={load}
                onPin={pinRow}
                onEdited={noteEdits}
                pinned={pinnedIds.has(r.id)}
                was={justEdited.get(r.id)?.before}
              />
            ))}
            {rows.length > limit && (
              <button
                type="button"
                data-testid="link-library-more"
                onClick={() => setLimit((n) => n + 100)}
                className="w-full px-3 py-2.5 text-xs text-[#3A4A3E] hover:bg-[#F2F1EC]"
              >Show {Math.min(100, rows.length - limit)} more</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
